/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import { useEffect, useState } from 'react';
import Dashboard, { DashboardRow } from '../../components/dashboard';
import { CompanyInsightsSidebar } from '../summary/insights/partials/sidebar/CompanyInsightsSidebar';
import { DataPeriods } from '../../types/universalTracker';
import { useLocation } from 'react-router-dom';
import { PeriodDropdown } from '../summary/insights/partials/PeriodDropdown';
import { Layout } from '../summary/insights/partials/Layout';
import { getLayout, layoutOptions } from '../summary/insights/utils/layout';
import { InsightPage } from '../summary/insights/utils/constants';
import { getMainDownloadCode, MainDownloadCode } from '../../config/app-config';
import { getInsightPage, isInsightLayoutPage } from '../summary/insights/utils/helpers';
import { Button, ButtonGroup } from 'reactstrap';
import { InitiativeCompany } from '../../types/initiative';
import { Portfolio } from '../../types/portfolio';
import { PortfolioCompanySummary } from './PortfolioCompanySummary';
import G17Client from '../../services/G17Client';
import { RequesterType } from '../../types/dataShare';
import { SurveyListDropdown } from '../../components/initiative/SurveyListDropdown';
import { SurveyListItem } from '../../types/survey';
import { hasFullScopeAccess } from '../../utils/dataShare';

interface Props {
  company: InitiativeCompany;
  hasButtons?: boolean;
  title?: string;
  className?: string;
  summaryPage: string | undefined;
  portfolio: Pick<Portfolio, '_id'>
}

export const PortfolioCompanyInsights = (props: Props) => {
  const { portfolio, company, summaryPage = '', title, className } = props;

  const location = useLocation();
  const period = new URLSearchParams(location.search).get('period') as DataPeriods;
  const mainDownloadCode = getMainDownloadCode(company.appConfigCode, company.permissionGroup); // Do not allow to change it
  const [selectedPack, setSelectedPack] = useState<MainDownloadCode>(mainDownloadCode);
  const summaryPageValid = getInsightPage(summaryPage);
  const [item, setItem] = useState<SurveyListItem | undefined>();
  const [surveyList, setSurveyList] = useState<SurveyListItem[]>([]);

  useEffect(() => {
    G17Client.getRequesterDataShare({
      requesterId: portfolio._id,
      requesterType: RequesterType.Portfolio,
      initiativeId: company._id,
    })
      .then((data) => {
        const surveyList = data.list;
        setSurveyList(surveyList);
        setItem(surveyList[0]);
      })
      .catch((e: Error) => {
        setSurveyList([]);
      });
  }, [company._id, portfolio._id]);

  const buttons = mainDownloadCode === 'sgx_metrics' && isInsightLayoutPage(summaryPage) ?
    [<ButtonGroup>
      {layoutOptions.map(option => {
        const isActive = selectedPack === option.code;
        return <Button
          key={option.code}
          outline={!isActive}
          active={isActive}
          color={'primary'}
          onClick={() => setSelectedPack(option.code)}>
          {option.name}
        </Button>
      })}</ButtonGroup>] : [];

  if (!summaryPage) {
    buttons.push(
      <SurveyListDropdown surveyList={surveyList} selectedItem={item} handleDropdownSelect={(item) => setItem(item)} />
    );
  }

  const hasButtons = buttons.length > 0;

  const layout = getLayout(summaryPageValid as InsightPage, selectedPack);

  return (
    <Dashboard className={`profile-dashboard ${className}`} hasSidebar={true}>
      <CompanyInsightsSidebar alignButtonRow={hasButtons} isPortfolioTracker={true} />
      {hasButtons ? <DashboardRow className='topbar-container' title={title} buttons={buttons} mb={2} /> : null}
      <>
        <DashboardRow
          title='Highlights'
          headingStyle={2}
          className='mt-4 text-ThemeHeadingLight'
          mb={2}
          buttons={summaryPage ? [
            <PeriodDropdown
              initiativeId={company._id}
              portfolioId={portfolio._id}
              selectedPack={selectedPack}
              period={period} />
          ] : []}
        />
        <Layout getHighlightButton={() => undefined} layout={layout} />
        {summaryPage ? null : (
          <PortfolioCompanySummary
            selectedPack={selectedPack}
            initiativeId={company._id}
            surveyId={item?._id || ''}
            portfolioId={portfolio._id}
            hasFullScopeAccess={hasFullScopeAccess(company)}
          />
        )}
      </>
    </Dashboard>
  );
}

